import { APP_DEFAULT_TIME_ZONE } from "@/lib/dates/zoned-input";

export { cn } from "cn";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatDateTime(timestamp: Date) {
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: APP_DEFAULT_TIME_ZONE,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(timestamp);
}

export function formatFullDate(date: Date) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: APP_DEFAULT_TIME_ZONE,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

export function formatTimeRange(startTime: Date, endTime: Date) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_DEFAULT_TIME_ZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${formatter.format(startTime)} - ${formatter.format(endTime)}`;
}

export function formatINR(minor: number): string {
  return `₹${(minor / 100).toFixed(2)}`;
}

const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat>();

export function formatMinorCurrency(
  minor: number,
  currency: string,
  locale = currency === "INR" ? "en-IN" : "en-US",
) {
  const formatterKey = `${locale}:${currency}`;
  let formatter = CURRENCY_FORMATTERS.get(formatterKey);

  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    });
    CURRENCY_FORMATTERS.set(formatterKey, formatter);
  }

  return formatter.format(minor / 100);
}

export function parseDateOnly(dateString: string | null | undefined) {
  if (!dateString) {
    return null;
  }

  const match = DATE_ONLY_PATTERN.exec(dateString);

  if (!match) {
    return null;
  }

  const [, yearString, monthString, dayString] = match;
  const year = Number(yearString);
  const month = Number(monthString);
  const day = Number(dayString);
  const parsedDate = new Date(year, month - 1, day);

  if (
    Number.isNaN(parsedDate.getTime()) ||
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getDate() !== day
  ) {
    return null;
  }

  parsedDate.setHours(0, 0, 0, 0);
  return parsedDate;
}

export function toDateOnlyString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function formatDateOnly(
  dateString: string | null | undefined,
  locale = "en-US",
) {
  const parsedDate = parseDateOnly(dateString);

  if (!parsedDate) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(parsedDate);
}

export function getAge(dateOfBirth: string | null, today = new Date()) {
  if (!dateOfBirth) {
    return null;
  }

  const birthDate = parseDateOnly(dateOfBirth);

  if (!birthDate) {
    return null;
  }

  const comparisonDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  let age = comparisonDate.getFullYear() - birthDate.getFullYear();
  const hasHadBirthdayThisYear =
    comparisonDate.getMonth() > birthDate.getMonth() ||
    (comparisonDate.getMonth() === birthDate.getMonth() &&
      comparisonDate.getDate() >= birthDate.getDate());

  if (!hasHadBirthdayThisYear) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}
